import { logger } from 'src/utils/logger';

export interface ISignaturePoint {
  x: number;
  y: number;
  time?: number;
  color?: string;
}

export type SignatureStrokes = ISignaturePoint[][];

export interface ISignaturePad {
  fromData: (strokes: SignatureStrokes) => void;
}

const isSignaturePoint = (value: unknown): value is ISignaturePoint => {
  if (typeof value !== 'object' || value === null) return false;
  const { x, y } = value as { x?: unknown; y?: unknown };
  return (
    typeof x === 'number' &&
    Number.isFinite(x) &&
    typeof y === 'number' &&
    Number.isFinite(y)
  );
};

const isSignatureStrokes = (value: unknown): value is SignatureStrokes =>
  Array.isArray(value) &&
  value.every(
    (stroke) => Array.isArray(stroke) && stroke.every(isSignaturePoint),
  );

const parseStored = (stored: unknown): unknown => {
  if (typeof stored !== 'string') return stored;
  const trimmed = stored.trim();
  if (trimmed === '') return null;
  return JSON.parse(trimmed);
};

/**
 * Replays a persisted signature onto a react-signature-canvas pad. Accepts the
 * stored JSON string or an already-parsed value, and only hands signature_pad
 * an array of strokes of `{ x, y }` points, because its `fromData` throws on
 * anything else and would take down the page rendering the pad. An empty value
 * clears the pad so strokes from a previously loaded signature do not linger.
 * @registry Safely replays a stored signature (JSON string or parsed strokes) onto a react-signature-canvas pad; clears it for an empty value and logs, instead of throwing, on malformed data.
 */
export const loadStoredSignature = (
  pad: ISignaturePad | null | undefined,
  stored: unknown,
): void => {
  if (!pad) return;

  let parsed: unknown;
  try {
    parsed = parseStored(stored);
  } catch (err) {
    logger.error('Stored signature is not valid JSON', { err });
    return;
  }

  const strokes = parsed ?? [];

  if (!isSignatureStrokes(strokes)) {
    logger.error('Stored signature is not a list of strokes', {
      storedType: typeof stored,
    });
    return;
  }

  try {
    pad.fromData(strokes);
  } catch (err) {
    logger.error('Failed to draw stored signature', { err });
  }
};
